
import React from 'react';
import { Link } from 'react-router-dom';
import { Product } from '../domain/types';
import { ProductCard } from './ProductCard';
import { ProductSkeleton } from './skeletons/ProductSkeleton';

interface HorizontalProductListProps {
    title: string;
    subtitle?: string;
    icon?: string;
    products: Product[];
    seeAllLink?: string;
    isLoading?: boolean;
    bgColor?: string;
    titleColor?: string;
    priority?: boolean;
}

export const HorizontalProductList = React.memo(({ 
    title, 
    subtitle = '', 
    products, 
    isLoading = false, 
    seeAllLink = '/catalog', 
    bgColor = 'bg-transparent', 
    titleColor = 'text-text-main dark:text-white',
    icon,
    priority = false,
    emptyMessage = 'Próximamente',
    showEmpty = true
}: HorizontalProductListProps & { emptyMessage?: string; showEmpty?: boolean }) => {
    if (isLoading) {
        return (
            <div className="py-4 space-y-4">
                <div className="px-4 space-y-2">
                    <div className="h-6 w-1/3 bg-gray-200 dark:bg-white/5 rounded-md animate-pulse"></div>
                    <div className="h-3 w-1/2 bg-gray-200 dark:bg-white/5 rounded-md animate-pulse"></div>
                </div>
                <div className="flex gap-4 px-4 overflow-hidden">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="min-w-[160px] w-[160px]">
                            <ProductSkeleton />
                        </div>
                    ))}
                </div>
            </div>
        );
    }
    
    if (products.length === 0) {
        if (!showEmpty) return null;
        
        return (
            <div className={`py-6 ${bgColor}`}>
                <div className="px-4 mb-4">
                    <div className="flex items-center gap-2 mb-1">
                        {icon && <span className={`material-symbols-outlined text-lg ${titleColor}`}>{icon}</span>}
                        <h2 className={`text-xl font-black tracking-tight ${titleColor} leading-none`}>{title}</h2>
                    </div>
                    {subtitle && <p className="text-xs text-text-secondary dark:text-gray-400 font-medium">{subtitle}</p>}
                </div>
                <div className="px-4">
                    <div className="bg-gray-50 dark:bg-white/5 rounded-2xl p-8 text-center border border-gray-100 dark:border-white/5">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-white/10 flex items-center justify-center">
                            <span className="material-symbols-outlined text-3xl text-gray-400">inventory_2</span>
                        </div>
                        <p className="text-gray-500 dark:text-gray-400 font-medium">{emptyMessage}</p>
                        <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Vuelve pronto para ver novidades</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`py-4 ${bgColor} transition-colors duration-300`}>
            <div className="px-4 flex justify-between items-end mb-4">
                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                        {icon && <span className={`material-symbols-outlined text-lg ${titleColor}`}>{icon}</span>}
                        <h2 className={`text-xl font-black tracking-tight ${titleColor} leading-none`}>{title}</h2>
                    </div>
                    {subtitle && <p className="text-xs text-text-secondary dark:text-gray-400 font-medium">{subtitle}</p>}
                </div>
                {seeAllLink && (
                    <Link to={seeAllLink} className="text-primary text-xs font-bold flex items-center gap-0.5 group">
                        Ver todo 
                        <span className="material-symbols-outlined text-sm group-hover:translate-x-0.5 transition-transform">chevron_right</span>
                    </Link>
                )}
            </div>
            
            <div className="flex overflow-x-auto gap-4 px-4 no-scrollbar pb-2">
                {products.map((p) => (
                    <ProductCard key={p.id} product={p} layout="horizontal" priority={priority} />
                ))}
            </div>
        </div>
    );
});
HorizontalProductList.displayName = 'HorizontalProductList';
